import { useState } from 'react';
import { Building2, MapPin, TrendingUp, AlertTriangle, Users } from 'lucide-react';

const BranchConsole = () => {
  const [selectedBranch, setSelectedBranch] = useState('dhaka');

  const branchData = {
    dhaka: {
      name: "Dhaka Central",
      status: "Peak Dinner Rush",
      statusColor: "text-emerald-400 bg-emerald-500/10 border-emerald-500/20",
      sales: "Tk. 1,42,500",
      labor: "12 Staff Clocked In",
      alert: "Milk stock running low in main storage"
    },
    banani: {
      name: "Banani Outlet",
      status: "Steady Traffic",
      statusColor: "text-blue-400 bg-blue-500/10 border-blue-500/20",
      sales: "Tk. 98,200",
      labor: "8 Staff Clocked In",
      alert: "All inventory parameters normal"
    },
    chittagong: {
      name: "Chittagong Hub",
      status: "Closing Checklist",
      statusColor: "text-amber-400 bg-amber-500/10 border-amber-500/20",
      sales: "Tk. 1,15,400",
      labor: "4 Staff Cleaning",
      alert: "Waste report pending from kitchen manager"
    }
  };

  return (
    <section className="py-24 bg-zinc-950 text-white relative border-t border-zinc-900" id="branches">
      
      {/* Background Lighting Field */}
      <div className="absolute top-0 right-1/4 w-[40%] h-[50%] bg-blue-600/5 rounded-full blur-[120px] pointer-events-none"></div>

      <div className="w-full px-6 sm:px-12 lg:px-16 mx-auto">
        
        {/* --- SECTION HEADER --- */}
        <div className="max-w-3xl mb-16 space-y-4">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-blue-500 font-mono block">
            // Multi-Location Operations
          </span>
          <h2 className="text-3xl sm:text-5xl font-black uppercase tracking-tight">
            Control your entire franchise <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-white via-zinc-400 to-zinc-600">
              from your pocket
            </span>
          </h2>
          <p className="text-zinc-400 text-base font-medium max-w-xl">
            Switch between outlets instantly. Monitor real-time register totals, active staff numbers, and critical kitchen alerts across town or across regions.
          </p>
        </div>

        {/* --- INTERACTIVE SIMULATOR FRAMEWORK --- */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-stretch">
          
          {/* Left Column: Branch Switcher Navigation */}
          <div className="space-y-3 flex flex-col justify-center">
            {Object.keys(branchData).map((key) => (
              <button
                key={key}
                onClick={() => setSelectedBranch(key)}
                className={`w-full text-left p-5 rounded-2xl border transition-all duration-300 flex items-center justify-between ${
                  selectedBranch === key
                  ? 'bg-zinc-900 border-blue-500 text-white shadow-xl shadow-blue-950/20'
                  : 'bg-zinc-900/30 border-zinc-800/80 text-zinc-400 hover:border-zinc-700'
                }`}
              >
                <div className="flex items-center gap-3.5">
                  <div className={`p-2 rounded-xl ${selectedBranch === key ? 'bg-blue-500/10 text-blue-400' : 'bg-zinc-950 text-zinc-500'}`}>
                    <Building2 className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-sm font-bold uppercase tracking-wide">{branchData[key].name}</p>
                    <p className="text-xs text-zinc-500 font-medium mt-0.5">Live Live Updates Available</p>
                  </div>
                </div>
                <MapPin className={`w-4 h-4 ${selectedBranch === key ? 'text-blue-400' : 'text-zinc-600'}`} />
              </button>
            ))}
          </div>

          {/* Right Columns: The Mock Control Terminal Viewport */}
          <div className="lg:col-span-2 bg-zinc-900 border border-zinc-800 rounded-3xl p-6 sm:p-10 flex flex-col justify-between relative overflow-hidden">
            
            {/* Top Bar Simulation */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-zinc-800/80 pb-6 w-full">
              <div className="space-y-1">
                <span className="text-[10px] font-mono tracking-widest text-zinc-500 uppercase font-bold">Active Branch Module</span>
                <h4 className="text-xl font-black uppercase tracking-tight text-white">{branchData[selectedBranch].name}</h4>
              </div>
              <div className={`px-3 py-1 rounded-lg text-xs font-bold border self-start sm:self-center ${branchData[selectedBranch].statusColor}`}>
                ● {branchData[selectedBranch].status}
              </div>
            </div>

            {/* Metrics Breakdown Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 my-10">
              
              {/* Sales Block */}
              <div className="bg-zinc-950 border border-zinc-800/60 rounded-2xl p-5 space-y-2">
                <div className="flex items-center gap-2 text-zinc-500">
                  <TrendingUp className="w-4 h-4 text-blue-400" />
                  <span className="text-xs font-bold uppercase tracking-wide">Live Net Register</span>
                </div>
                <p className="text-2xl sm:text-3xl font-black text-white tracking-tight">{branchData[selectedBranch].sales}</p>
              </div>

              {/* Labor Block */}
              <div className="bg-zinc-950 border border-zinc-800/60 rounded-2xl p-5 space-y-2">
                <div className="flex items-center gap-2 text-zinc-500">
                  <Users className="w-4 h-4 text-cyan-400" />
                  <span className="text-xs font-bold uppercase tracking-wide">Floor Coverage</span>
                </div>
                <p className="text-2xl sm:text-3xl font-black text-white tracking-tight">{branchData[selectedBranch].labor}</p>
              </div>

            </div>

            {/* Bottom Alert Strip */}
            <div className="bg-zinc-950/50 border border-zinc-800/80 rounded-xl p-4 flex items-center gap-3.5 text-sm">
              <AlertTriangle className={`w-5 h-5 shrink-0 ${selectedBranch === 'dhaka' ? 'text-amber-500' : 'text-zinc-600'}`} />
              <p className="text-zinc-300 font-medium">
                <span className="text-zinc-500 font-bold uppercase text-xs font-mono mr-2">[ Notification ]</span>
                {branchData[selectedBranch].alert}
              </p>
            </div>

          </div>

        </div>

      </div>
    </section>
  );
};

export default BranchConsole;